import React, { useState, useEffect } from 'react';
import { useTranslation } from 'utils/i18n';
import { AZURE_REGIONS } from 'utils/azureRegions';
import { parseDomain, mapRegionCodeToName } from 'utils/domainParser';
import { Icons } from 'components/Icons';
import './RegionProviderModal.css';

// Provider options
const PROVIDERS = [
  { code: 'az', name: 'Azure', enabled: true },
  { code: 'aws', name: 'AWS', enabled: false },
  { code: 'gcp', name: 'Google Cloud', enabled: false },
];

const RegionProviderModal = ({ onSelect, onClose, initialProvider = null, initialRegion = null }) => {
  const { t } = useTranslation();
  
  // Debug logging
  useEffect(() => {
    console.log('[RegionProviderModal] Modal rendered', {
      initialProvider,
      initialRegion,
      hostname: typeof window !== 'undefined' ? window.location.hostname : 'N/A',
      port: typeof window !== 'undefined' ? window.location.port : 'N/A'
    });
  }, []);
  
  // Initialize with defaults or provided values
  const getInitialProvider = () => {
    if (initialProvider) return initialProvider;
    // Try to get from localStorage first
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const savedProvider = localStorage.getItem('selectedProvider');
      if (savedProvider && PROVIDERS.find(p => p.code === savedProvider && p.enabled)) {
        return savedProvider;
      }
    }
    // Try to get from URL params
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlProvider = urlParams.get('provider');
      if (urlProvider && PROVIDERS.find(p => p.code === urlProvider && p.enabled)) {
        return urlProvider;
      }
    }
    // Try to get from domain
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const domainInfo = parseDomain(hostname);
      if (domainInfo && PROVIDERS.find(p => p.code === domainInfo.provider)?.enabled) {
        return domainInfo.provider;
      }
    }
    // Always return first enabled provider as default
    const firstEnabledProvider = PROVIDERS.find(p => p.enabled);
    return firstEnabledProvider ? firstEnabledProvider.code : 'az';
  };
  
  const getInitialRegion = () => {
    if (initialRegion && AZURE_REGIONS.find((r) => r.value === initialRegion && r.enabled)) {
      return initialRegion;
    }
    // Try to get from localStorage first
    if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
      const savedRegion = localStorage.getItem('selectedRegion');
      if (savedRegion && AZURE_REGIONS.find(r => r.value === savedRegion && r.enabled)) {
        return savedRegion;
      }
    }
    // Try to get from URL params
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      const urlRegion = urlParams.get('region');
      if (urlRegion && AZURE_REGIONS.find(r => r.value === urlRegion && r.enabled)) {
        return urlRegion;
      }
    }
    // Try to get from domain
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      const domainInfo = parseDomain(hostname);
      if (domainInfo) {
        const regionName = mapRegionCodeToName(domainInfo.region);
        if (regionName && AZURE_REGIONS.find(r => r.value === regionName)?.enabled) {
          return regionName;
        }
      }
    }
    // Always return first enabled region as default
    const firstEnabledRegion = AZURE_REGIONS.find(r => r.enabled);
    return firstEnabledRegion ? firstEnabledRegion.value : 'germanywestcentral';
  };
  
  const [selectedProvider, setSelectedProvider] = useState(() => getInitialProvider());
  const [selectedRegion, setSelectedRegion] = useState(() => getInitialRegion());
  const [savedLogins, setSavedLogins] = useState([]);

  // Load saved logins for this host (provider/region combinations user has logged into)
  useEffect(() => {
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') return;
    const hostname = window.location.hostname;
    const raw = localStorage.getItem('platformAuthLogins');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        const forHost = parsed.filter((entry) => entry && entry.host === hostname);
        setSavedLogins(forHost);
      }
    } catch (e) {
      console.warn('[RegionProviderModal] Failed to parse platformAuthLogins from localStorage', e);
    }
  }, []);

  const handleConfirm = () => {
    // Store selection (only if localStorage is available)
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('selectedProvider', selectedProvider);
      localStorage.setItem('selectedRegion', selectedRegion);
    }
    
    // Call onSelect callback with selection
    if (onSelect) {
      onSelect({
        provider: selectedProvider,
        region: selectedRegion
      });
    }
  };

  const isConfirmEnabled = () => {
    // Check if selected provider is enabled
    const provider = PROVIDERS.find(p => p.code === selectedProvider);
    if (!provider || !provider.enabled) {
      return false;
    }
    
    // Check if selected region is enabled
    const region = AZURE_REGIONS.find(r => r.value === selectedRegion);
    if (!region || !region.enabled) {
      return false;
    }
    
    return true;
  };

  return (
    <div className="region-provider-modal-overlay" data-theme="light">
      <div className="region-provider-modal" data-theme="light">
        <div className="region-provider-modal-content">
          <div className="region-provider-modal-header">
            {onClose && (
              <button
                className="region-provider-modal-close"
                onClick={onClose}
                aria-label={t('regionProviderModal.close', 'Close')}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  fontSize: '1.5rem',
                  color: '#666',
                  padding: '0.5rem',
                  lineHeight: 1
                }}
              >
                ×
              </button>
            )}
            <h2 className="region-provider-modal-title">
              {t('regionProviderModal.title', 'Select Hosting Configuration')}
            </h2>
            <p className="region-provider-modal-subtitle">
              {t('regionProviderModal.subtitle', 'Before you can sign in, please select where your data and processes will be hosted')}
            </p>
            <div className="region-provider-modal-description">
              <p>
                {t('regionProviderModal.description', 'You are not currently logged in. To proceed, you need to choose a cloud provider and region. This determines where your data will be stored and where all your processes will run. This selection helps ensure compliance with data residency requirements and optimizes performance based on your location.')}
              </p>
            </div>
          </div>

          {savedLogins && savedLogins.length > 0 && (
            <div className="saved-logins-section">
              <div className="saved-logins-header">
                <h3 className="saved-logins-title">
                  {t('regionProviderModal.savedLoginsTitle', 'Previously used environments')}
                </h3>
                <p className="saved-logins-subtitle">
                  {t(
                    'regionProviderModal.savedLoginsSubtitle',
                    'Quickly jump back into an environment you have already used.'
                  )}
                </p>
              </div>
              <div className="saved-logins-table" role="list">
                <div className="saved-logins-row saved-logins-row-header">
                  <span className="saved-logins-cell saved-logins-cell-provider">
                    {t('regionProviderModal.columnProvider', 'Provider')}
                  </span>
                  <span className="saved-logins-cell saved-logins-cell-region">
                    {t('regionProviderModal.columnRegion', 'Region')}
                  </span>
                  <span className="saved-logins-cell saved-logins-cell-last-used">
                    {t('regionProviderModal.columnLastUsed', 'Last used')}
                  </span>
                </div>
                {savedLogins.map((entry, index) => {
                  const provider = PROVIDERS.find((p) => p.code === entry.provider);
                  const region = AZURE_REGIONS.find((r) => r.value === entry.region);
                  if (!provider || !region) return null;
                  const isSelected =
                    selectedProvider === entry.provider && selectedRegion === entry.region;
                  const lastUsedLabel = entry.lastUsed
                    ? new Date(entry.lastUsed).toLocaleString()
                    : t('regionProviderModal.lastUsedUnknown', 'Unknown');

                  return (
                    <button
                      key={`${entry.provider}-${entry.region}-${index}`}
                      type="button"
                      className={`saved-logins-row saved-logins-row-button ${
                        isSelected ? 'saved-logins-row-selected' : ''
                      }`}
                      onClick={() => {
                        setSelectedProvider(entry.provider);
                        setSelectedRegion(entry.region);
                      }}
                      aria-label={t(
                        'regionProviderModal.savedLoginAria',
                        'Use {provider} in {region}',
                        {
                          provider: provider.name,
                          region: region.label,
                        }
                      )}
                    >
                      <span className="saved-logins-cell saved-logins-cell-provider">
                        {provider.name}
                      </span>
                      <span className="saved-logins-cell saved-logins-cell-region">
                        {region.label}
                      </span>
                      <span className="saved-logins-cell saved-logins-cell-last-used">
                        {lastUsedLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <div className="region-provider-selection">
            {/* Provider Selection */}
            <div className="selection-section">
              <label className="selection-label">
                {t('regionProviderModal.providerLabel', 'Cloud Provider')}
              </label>
              <div className="provider-options">
                {PROVIDERS.map((provider) => (
                  <button
                    key={provider.code}
                    className={`provider-option ${selectedProvider === provider.code ? 'selected' : ''} ${!provider.enabled ? 'disabled' : ''}`}
                    onClick={() => provider.enabled && setSelectedProvider(provider.code)}
                    disabled={!provider.enabled}
                    aria-label={`${provider.name} ${provider.enabled ? '' : '(Coming Soon)'}`}
                  >
                    <div className="provider-option-content">
                      <div className="provider-option-icon">
                        {selectedProvider === provider.code && (
                          <Icons.Check size={20} />
                        )}
                      </div>
                      <div className="provider-option-info">
                        <span className="provider-option-name">{provider.name}</span>
                        {!provider.enabled && (
                          <span className="provider-option-badge">{t('regionProviderModal.comingSoon', 'Coming Soon')}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Region Selection */}
            <div className="selection-section">
              <label className="selection-label">
                {t('regionProviderModal.regionLabel', 'Region')}
              </label>
              <div className="region-options">
                {AZURE_REGIONS.map((region) => (
                  <button
                    key={region.value}
                    className={`region-option ${selectedRegion === region.value ? 'selected' : ''} ${!region.enabled ? 'disabled' : ''}`}
                    onClick={() => region.enabled && setSelectedRegion(region.value)}
                    disabled={!region.enabled}
                    aria-label={`${region.label} ${region.enabled ? '' : '(Coming Soon)'}`}
                  >
                    <div className="region-option-content">
                      <div className="region-option-icon">
                        {selectedRegion === region.value && (
                          <Icons.Check size={20} />
                        )}
                      </div>
                      <div className="region-option-info">
                        <span className="region-option-name">{region.label}</span>
                        {!region.enabled && (
                          <span className="region-option-badge">{t('regionProviderModal.comingSoon', 'Coming Soon')}</span>
                        )}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="region-provider-modal-footer">
          <button
            className="region-provider-confirm-button"
            onClick={handleConfirm}
            disabled={!isConfirmEnabled()}
          >
            {t('regionProviderModal.confirm', 'Continue')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegionProviderModal;

